import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useOfflineStore } from '../store/offlineStore';
import { drainQueue } from '../utils/offlineSync';
import { countPending } from '../utils/offlineDB';

const CSS = `
@keyframes ob-slide-in  { from { transform:translateY(100%); opacity:0; } to { transform:translateY(0); opacity:1; } }
@keyframes ob-slide-out { from { transform:translateY(0);    opacity:1; } to { transform:translateY(100%); opacity:0; } }
@keyframes ob-spin { to { transform: rotate(360deg); } }
.ob-bar {
  position:fixed; bottom:0; left:0; right:0; z-index:9998;
  display:flex; align-items:center; justify-content:center; gap:8px;
  padding:10px 16px;
  font-size:13px; font-weight:500; letter-spacing:0.01em;
  backdrop-filter:blur(8px);
  animation:ob-slide-in 0.3s ease both;
}
.ob-bar.leaving { animation:ob-slide-out 0.3s ease both; }
.ob-bar.offline {
  background:rgba(30,20,10,0.92);
  border-top:1px solid rgba(245,158,11,0.4);
  color:#fcd34d;
}
.ob-bar.syncing {
  background:rgba(10,20,35,0.92);
  border-top:1px solid rgba(59,130,246,0.4);
  color:#93c5fd;
}
.ob-bar.back {
  background:rgba(10,25,15,0.92);
  border-top:1px solid rgba(34,197,94,0.4);
  color:#4ade80;
}
.ob-payment-reminder {
  position:fixed; bottom:0; left:0; right:0; z-index:9997;
  display:flex; align-items:center; justify-content:center; gap:10px; flex-wrap:wrap;
  padding:10px 16px;
  background:rgba(30,15,0,0.95);
  border-top:2px solid rgba(251,191,36,0.6);
  color:#fde68a;
  font-size:13px; font-weight:500;
  animation:ob-slide-in 0.3s ease both;
}
.ob-payment-reminder button {
  padding:4px 12px; border-radius:6px; border:1px solid rgba(251,191,36,0.5);
  background:rgba(251,191,36,0.15); color:#fde68a; cursor:pointer; font-size:12px;
  font-weight:600; white-space:nowrap;
}
.ob-payment-reminder button:hover { background:rgba(251,191,36,0.3); }
.ob-payment-reminder .ob-go-billing {
  background:rgba(251,191,36,0.25); border-color:rgba(251,191,36,0.7);
}
.ob-spinner {
  display:inline-block; width:12px; height:12px; border-radius:50%;
  border:2px solid rgba(147,197,253,0.3); border-top-color:#93c5fd;
  animation:ob-spin 0.8s linear infinite; flex-shrink:0;
}
@media (max-width:480px) { .ob-bar { font-size:12px; padding:9px 12px; } .ob-payment-reminder { font-size:12px; } }
`;

const Dot = ({ color }) => (
  <span style={{ display:'inline-block', width:7, height:7, borderRadius:'50%', background:color, flexShrink:0 }} />
);

function useOfflineLifecycle() {
  const {
    setOnline, setPendingCount, setSyncing, setSyncedAt,
    setSyncProgress, setSyncedCount, setCheckoutsSynced,
  } = useOfflineStore();
  const syncingRef = useRef(false);

  const runSync = React.useCallback(async () => {
    if (syncingRef.current) return;
    const total = await countPending();
    if (!total) return;
    syncingRef.current = true;
    setSyncing(true);
    setSyncProgress({ current: 0, total });
    try {
      const { synced, checkoutsSynced } = await drainQueue(({ synced: cur }) => {
        setSyncProgress({ current: cur, total });
      });
      if (synced) {
        setSyncedCount(synced);
        setSyncedAt(Date.now());
      }
      if (checkoutsSynced > 0) {
        setCheckoutsSynced(checkoutsSynced);
      }
    } finally {
      const remaining = await countPending();
      setPendingCount(remaining);
      setSyncProgress({ current: 0, total: 0 });
      setSyncing(false);
      syncingRef.current = false;
    }
  }, [setSyncing, setSyncedAt, setSyncProgress, setSyncedCount, setPendingCount, setCheckoutsSynced]);

  useEffect(() => {
    countPending().then(setPendingCount);

    const doPrefetch = () =>
      import('../utils/prefetchOfflineData').then(({ prefetchOfflineData }) =>
        prefetchOfflineData().catch(() => {})
      );

    const handleOnline = () => {
      setOnline(true);
      runSync();
      doPrefetch();
    };
    const handleOffline = () => setOnline(false);
    const handleMessage = (ev) => {
      if (ev.data?.type === 'SYNC_QUEUE') runSync();
    };

    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    navigator.serviceWorker?.addEventListener('message', handleMessage);

    if (navigator.onLine) {
      runSync();
      // Warm the SW cache on initial load so key pages work offline right away
      doPrefetch();
    }

    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [setOnline, setPendingCount, runSync]);
}

export default function OfflineBanner() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  useOfflineLifecycle();

  const navigate = useNavigate();
  const { isOnline, pendingCount, syncing, syncedAt, syncProgress, syncedCount, checkoutsSynced, clearCheckoutsSynced } = useOfflineStore();

  const [phase, setPhase]     = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [showPaymentReminder, setShowPaymentReminder] = useState(false);
  const timerRef    = useRef(null);
  const syncedAtRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!isOnline) {
      setLeaving(false); setPhase('offline'); return;
    }
    if (syncing) {
      setLeaving(false); setPhase('syncing'); return;
    }
    if (syncedAt) {
      setLeaving(false); setPhase('back');
      timerRef.current = setTimeout(() => setLeaving(true),  3000);
      timerRef.current = setTimeout(() => setPhase(null),    3300);
    }
    if (isOnline && !syncing && !syncedAt) {
      setPhase(null);
    }
    return () => clearTimeout(timerRef.current);
  }, [isOnline, syncing, syncedAt]);

  // After queue drain completes (syncedAt is a new timestamp), invalidate all active queries
  // so every open page immediately shows post-sync live data without a manual reload.
  useEffect(() => {
    if (syncedAt && syncedAt !== syncedAtRef.current) {
      syncedAtRef.current = syncedAt;
      qc.invalidateQueries();
    }
  }, [syncedAt, qc]);

  // Show payment reminder after the "back" banner fades, if checkouts were synced
  useEffect(() => {
    if (checkoutsSynced > 0 && syncedAt) {
      const timer = setTimeout(() => setShowPaymentReminder(true), 3500);
      return () => clearTimeout(timer);
    }
  }, [syncedAt, checkoutsSynced]);

  const dismissPaymentReminder = () => {
    setShowPaymentReminder(false);
    clearCheckoutsSynced();
  };

  const goToBilling = () => {
    dismissPaymentReminder();
    navigate('/billing');
  };

  // ── Banner text — each phase shows a translated string with embedded count ──

  // "Offline — 3 changes queued" OR generic offline message
  const offlineText = pendingCount > 0
    ? t('offline.banner_queued', { count: pendingCount, defaultValue: `You're offline — {{count}} queued` })
    : t('offline.banner', "You're offline — showing cached data");

  // "Syncing 2 of 5…" OR generic syncing message
  const { current, total } = syncProgress;
  const syncingText = total > 0
    ? t('offline.syncing_progress', { current, total, defaultValue: 'Syncing {{current}} of {{total}}…' })
    : t('offline.syncing', 'Syncing changes…');

  // "Back online — 3 synced" OR generic back-online message
  const backText = syncedCount > 0
    ? t('offline.reconnected_count', { count: syncedCount, defaultValue: 'Back online — {{count}} synced' })
    : t('offline.reconnected', 'Back online — all changes synced');

  const paymentReminderText = t('offline.payment_reminder', {
    count: checkoutsSynced,
    defaultValue: 'Checkout synced — please record payment in Billing for {{count}} booking(s)',
  });

  return (
    <>
      <style>{CSS}</style>
      {phase && (
        <div className={`ob-bar ${phase}${leaving ? ' leaving' : ''}`}>
          {phase === 'offline' && <><Dot color="#fbbf24" />{offlineText}</>}
          {phase === 'syncing' && <><span className="ob-spinner" />{syncingText}</>}
          {phase === 'back'    && <><Dot color="#4ade80" />{backText}</>}
        </div>
      )}
      {showPaymentReminder && (
        <div className="ob-payment-reminder">
          <span>💳 {paymentReminderText}</span>
          <button className="ob-go-billing" onClick={goToBilling}>
            {t('offline.go_billing', 'Go to Billing')}
          </button>
          <button onClick={dismissPaymentReminder}>✕</button>
        </div>
      )}
    </>
  );
}
